import { ActivityFeed } from "@/components/Activity/ActivityFeed";
import { AlertsPanel } from "@/components/Alerts/AlertsPanel";
import { OnboardingFlow } from "@/components/Onboarding/OnboardingFlow";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useGetGitHubInstallations } from "@/hooks/useGitHubApp";
import { usePlans } from "@/hooks/usePlans";
import { completeOnboarding, getCurrentUser } from "@/lib/user/api";
import { getDeployStatusColor } from "@/lib/deploy/utils";
import { PATH } from "@/lib/enums";
import { humanizeString } from "@/lib/utils";
import { getDeployCount, getRecentDeploys } from "@/lib/deploy/api";
import { getMyOrganization } from "@/lib/organization/api";
import { getRecentAlert } from "@/lib/alert/api";
import { getRecentActivity } from "@/lib/activity/api";
import type {
  ActivityEvent,
  Alert as AlertType,
  Deploy,
  Organization,
  User,
} from "@/types";
import {
  Activity,
  AlertTriangle,
  DollarSign,
  ExternalLink,
  GitBranch,
  Github,
  Plus,
  Rocket,
  TrendingUp,
  Users,
} from "lucide-react";
import {
  useMutation,
  useQueryClient,
  useSuspenseQueries,
} from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

type DeployTotals = {
  successful: number;
  failed: number;
};

type ApiResponse<T> = {
  data: T;
};

type ApiListResponse<T> = {
  data: {
    items?: T;
  };
};

type DashboardLocationState = {
  signup?: boolean;
};

export function DashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as DashboardLocationState | null;
  const isSignUpFlow = Boolean(locationState?.signup);
  const { plans } = usePlans();
  const [
    currentUserQuery,
    deployTotalsQuery,
    recentDeploysQuery,
    organizationQuery,
    alertsQuery,
    activityQuery,
  ] = useSuspenseQueries({
    queries: [
      {
        queryKey: ["currentUser"] as const,
        queryFn: getCurrentUser,
        select: (res?: ApiResponse<User>) => res?.data,
        staleTime: 5000, // Cache for 5 seconds
      },
      {
        queryKey: ["deploy", "active"] as const,
        queryFn: getDeployCount,
        select: (res?: ApiResponse<DeployTotals | null>) => ({
          successful: res?.data?.successful ?? 0,
          failed: res?.data?.failed ?? 0,
        }),
      },
      {
        queryKey: ["deploy", "recent"] as const,
        queryFn: getRecentDeploys,
        select: (res?: ApiListResponse<Deploy[]>) =>
          (res?.data?.items as Deploy[]) ?? [],
      },
      {
        queryKey: ["organizations"] as const,
        queryFn: getMyOrganization,
        select: (res?: ApiResponse<Organization>) => res?.data,
      },
      {
        queryKey: ["alert", "recent"] as const,
        queryFn: getRecentAlert,
        select: (res?: ApiListResponse<AlertType[]>) =>
          (res?.data?.items as AlertType[]) ?? [],
      },
      {
        queryKey: ["activity", "recent"] as const,
        queryFn: getRecentActivity,
        select: (res?: ApiListResponse<ActivityEvent[]>) =>
          (res?.data?.items as ActivityEvent[]) ?? [],
      },
    ],
  });

  const currentUser = currentUserQuery.data;
  const deployTotals = deployTotalsQuery.data;
  const recentDeploys = recentDeploysQuery.data;
  const organization = organizationQuery.data;
  const recentAlerts = alertsQuery.data;
  const recentActivity = activityQuery.data;

  const [showOnboarding, setShowOnboarding] = useState(
    !(currentUser?.onboardCompleted ?? false),
  );
  const queryClient = useQueryClient();
  const completeOnboardingMutation = useMutation({
    mutationFn: completeOnboarding,
    onSuccess: () => {
      queryClient.setQueryData<User | undefined>(["currentUser"], (prev) =>
        prev ? { ...prev, onboardCompleted: true } : prev,
      );
      setShowOnboarding(false);
    },
  });
  // Only enable polling during onboarding when GitHub isn't connected yet
  const shouldPollGitHub = showOnboarding;
  const gitHubInstallationsQuery = useGetGitHubInstallations(
    true,
    shouldPollGitHub,
  );

  // Auto-complete onboarding when GitHub is connected
  useEffect(() => {
    if (
      showOnboarding &&
      gitHubInstallationsQuery.isSuccess &&
      (gitHubInstallationsQuery.data?.items?.length ?? 0) > 0 &&
      !completeOnboardingMutation.isPending
    ) {
      handleOnboardingComplete();
    }
  }, [
    showOnboarding,
    gitHubInstallationsQuery.isSuccess,
    gitHubInstallationsQuery.data?.items?.length,
    completeOnboardingMutation.isPending,
  ]);

  if (isSignUpFlow) {
    return (
      <div className="min-h-full flex items-center justify-center bg-[var(--bg)] px-4 py-12">
        <Card className="w-full max-w-2xl holo-card border-[var(--primary)]/50">
          <CardHeader className="text-center space-y-3">
            <CardTitle className="text-3xl font-semibold tracking-tight text-[var(--text-main)] ">
              INITIATE_VIBE_DEPLOY
            </CardTitle>
            <CardDescription className="text-[var(--text-subtle)]">
              System ready. Establish connection with GitHub repository to
              commence deployment sequence.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <p className="text-sm text-[var(--text-subtle)] ">
              &gt; Vibe Deploy orchestrator online.
              <br />
              &gt; Waiting for project definition...
            </p>
            <div className="flex flex-col gap-3 md:flex-row md:justify-center">
              <Button
                className="cyber-button h-12 flex-1"
                onClick={() =>
                  navigate(PATH.CREATE_DEPLOY, {
                    state: { from: location.pathname, search: location.search },
                  })
                }
              >
                <Plus className="h-4 w-4 mr-2" />
                CREATE APPLICATION
              </Button>
              <Button
                variant="outline"
                className="cyber-button h-12 flex-1"
                onClick={() => navigate(PATH.GITHUB)}
              >
                LINK GITHUB
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentPlan = useMemo(
    () => (plans || []).find((p) => p.id === organization?.planId),
    [plans, organization?.planId],
  );
  const totalCredits =
    organization?.credits?.reduce(
      (sum, credit) => sum + credit.amountCents,
      0,
    ) ?? 0;
  const stats = [
    {
      title: "Active Apps",
      value: deployTotals.successful,
      icon: Rocket,
      description: "Running",
      trend: "neutral",
      color: "cyan",
      textColor: "text-[var(--primary)]",
    },
    {
      title: "Total Users",
      value: 1,
      icon: Users,
      description: `${organization?.plan?.maxUsers ?? 0} max`,
      trend: "neutral",
      color: "blue",
      textColor: "text-blue-400",
    },
    {
      title: "Monthly Spend",
      value: `$${((currentPlan?.costPerUserCents || 0) / 100).toFixed(2)}`,
      icon: DollarSign,
      description: "Plan cost",
      trend: "neutral",
      color: "green",
      textColor: "text-[var(--success)]",
    },
    {
      title: "Failed Apps",
      value: deployTotals.failed,
      icon: AlertTriangle,
      description: "30 days",
      trend: "neutral",
      color: "red",
      textColor: "text-[var(--error)]",
    },
  ];

  const handleOnboardingComplete = async () => {
    try {
      await completeOnboardingMutation.mutateAsync();
      // Query client will update the cache, but manually invalidate to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
    } catch (error) {
      console.error("Failed to mark onboarding as complete", error);
    }
  };

  const shouldShowGitHubConnectBanner =
    gitHubInstallationsQuery.isSuccess &&
    (gitHubInstallationsQuery.data?.items?.length ?? 0) === 0;

  return (
    <div className="min-h-full pb-4 bg-[var(--bg)]">
      <div className="p-4 md:p-6 lg:p-8 space-y-4 md:space-y-6 lg:space-y-8">
        {shouldShowGitHubConnectBanner && (
          <Alert className="border-[var(--primary)]/40 bg-[var(--primary)]/10 text-[var(--primary)]">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <AlertTitle className="text-[var(--primary)] ">
                  GITHUB_CONNECTION_REQUIRED
                </AlertTitle>
                <AlertDescription className="text-[var(--primary)]/80 text-xs">
                  Connect GitHub to link repositories.
                </AlertDescription>
              </div>
              <Button
                className="cyber-button self-start sm:self-auto"
                onClick={() => navigate(PATH.GITHUB)}
              >
                CONNECT GITHUB
              </Button>
            </div>
          </Alert>
        )}

        {/* Quick Deploy Section - Scanline Effect */}
        <Card className="holo-card border-[var(--primary)]/30 scanline overflow-hidden">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-3 text-xl  uppercase tracking-wide text-[var(--text-main)]">
                  <span className="text-[var(--primary)]">&gt;</span> Quick
                  Deploy
                </CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button
                onClick={() =>
                  navigate("/create-deploy", {
                    state: { from: location.pathname, search: location.search },
                  })
                }
                className="cyber-button h-auto p-6 flex flex-col items-center gap-3 text-left group hover:scale-[1.01] transition-all bg-[var(--bg-alt)]/50 border-[var(--border-color)]"
              >
                <div className="flex items-center gap-3 w-full">
                  <Github className="h-8 w-8 text-[var(--text-subtle)] group-hover:text-[var(--primary)] transition-colors" />
                  <div className="flex-1">
                    <div className="font-semibold text-[var(--text-main)] group-hover:text-[var(--primary)] ">
                      DEPLOY_FROM_GITHUB
                    </div>
                  </div>
                </div>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid - HUD Style */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 lg:gap-6 mb-4 md:mb-6 lg:mb-8">
          {stats.map((stat) => (
            <Card
              key={stat.title}
              className="holo-card group hover:translate-y-[-2px] transition-all duration-300 border-[var(--border-color)] bg-[var(--bg-alt)]/30"
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-3 md:p-4 lg:p-6">
                <CardTitle className="text-xs md:text-xs  uppercase tracking-widest text-[var(--text-subtle)]">
                  {stat.title}
                </CardTitle>
                <stat.icon
                  className={`h-3 w-3 md:h-4 md:w-4 ${stat.textColor} opacity-70`}
                />
              </CardHeader>
              <CardContent className="p-3 md:p-4 lg:p-6 pt-0">
                <div
                  className={`text-xl md:text-2xl lg:text-3xl font-bold  ${stat.textColor}`}
                >
                  {stat.value}
                </div>
                <p className="text-[10px] uppercase tracking-wide text-[var(--text-subtle)] flex items-center mt-1 md:mt-2 opacity-60">
                  {stat.trend === "up" && (
                    <TrendingUp className="h-3 w-3 mr-1 text-[var(--success)]" />
                  )}
                  <span className="truncate">{stat.description}</span>
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
          {/* Recent Deploys - Timeline Style */}
          <Card className="holo-card h-full">
            <CardHeader className="border-b border-[var(--border-color)] pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-3 text-lg  uppercase tracking-wide">
                    <Activity className="h-4 w-4 text-[var(--primary)]" />
                    RECENT_ACTIVITY_LOG
                  </CardTitle>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="cyber-button text-[10px] h-8"
                  onClick={() => navigate(PATH.APPLICATIONS)}
                >
                  VIEW_ALL
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {!recentDeploys || recentDeploys.length === 0 ? (
                <div className="text-center py-8">
                  <Rocket className="h-12 w-12 text-[var(--text-subtle)] mx-auto mb-4 opacity-20" />
                  <p className="text-[var(--text-subtle)] text-sm mb-4 ">
                    NO_DATA_FOUND. INITIATE_DEPLOYMENT.
                  </p>
                  <Button
                    onClick={() =>
                      navigate("/create-deploy", {
                        state: {
                          from: location.pathname,
                          search: location.search,
                        },
                      })
                    }
                    className="cyber-button"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    DEPLOY
                  </Button>
                </div>
              ) : (
                <div className="space-y-0 relative pl-4">
                  {/* Vertical Timeline Line */}
                  <div className="absolute left-[19px] top-2 bottom-6 w-px bg-[var(--border-color)]"></div>

                  {recentDeploys.map((deploy) => (
                    <div
                      key={deploy.id}
                      className="relative pl-8 pb-6 last:pb-0 group cursor-pointer"
                      onClick={() =>
                        navigate(`${PATH.APPLICATIONS}/${deploy.id}`)
                      }
                    >
                      {/* Timeline Node */}
                      <div
                        className={`absolute left-[15px] top-1.5 h-2 w-2 rounded-full border border-[var(--bg)] ring-2 ${deploy.status === "success" ? "bg-[var(--success)] ring-[var(--success)]/20" : deploy.status === "failed" ? "bg-[var(--error)] ring-[var(--error)]/20" : "bg-[var(--primary)] ring-[var(--primary)]/20"} transition-all group-hover:scale-125 z-10`}
                      ></div>

                      <div className="flex items-start justify-between rounded border border-transparent p-2 -mt-2 -ml-2 group-hover:bg-[var(--bg-alt)] group-hover:border-[var(--border-color)] transition-all">
                        <div className="flex-1 min-w-0 space-y-1">
                          <div className="flex items-center gap-2">
                            <p className=" text-sm text-[var(--text-main)] truncate group-hover:text-[var(--primary)] transition-colors">
                              {deploy.repoName}
                            </p>
                            <span className="text-[10px] text-[var(--text-subtle)]  opacity-50">
                              {deploy.id.substring(0, 7)}
                            </span>
                          </div>

                          <div className="flex items-center gap-2">
                            <GitBranch className="h-3 w-3 text-[var(--text-subtle)]" />
                            <p className="text-xs text-[var(--text-subtle)] ">
                              {deploy.branch}
                            </p>
                            <span className="text-[var(--text-subtle)]/30 text-xs">
                              |
                            </span>
                            <p className="text-xs text-[var(--text-subtle)]  uppercase">
                              {deploy.env}
                            </p>
                          </div>

                          <p className="text-[10px] text-[var(--text-subtle)] pt-1 opacity-60 ">
                            {new Date(deploy.createdAt).toLocaleDateString()}{" "}
                            {new Date(deploy.createdAt).toLocaleTimeString()}
                          </p>
                        </div>

                        <div className="flex flex-col items-end gap-2 flex-shrink-0">
                          <Badge
                            className={`${getDeployStatusColor(
                              deploy.status,
                            )} text-[10px]  uppercase rounded-sm border-0`}
                          >
                            {humanizeString(deploy.status)}
                          </Badge>
                          <ExternalLink className="h-3 w-3 text-[var(--text-subtle)] group-hover:text-[var(--primary)] opacity-0 group-hover:opacity-100 transition-all" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* System Information - HUD Data Block */}
          <Card className="holo-card h-full">
            <CardHeader className="border-b border-[var(--border-color)] pb-4">
              <CardTitle className="text-lg  uppercase tracking-wide flex items-center gap-2">
                <Users className="h-4 w-4 text-[var(--accent)]" />
                ORG_METADATA
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-6  text-sm">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <label className="text-[10px] uppercase text-[var(--text-subtle)] tracking-wider">
                      Organization ID
                    </label>
                    <p className="text-[var(--text-main)] border-b border-[var(--border-color)] pb-1">
                      {organization?.name}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] uppercase text-[var(--text-subtle)] tracking-wider">
                      Subscription Plan
                    </label>
                    <p className="text-[var(--primary)] border-b border-[var(--border-color)] pb-1">
                      {organization?.plan?.name ?? "N/A"}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <label className="text-[10px] uppercase text-[var(--text-subtle)] tracking-wider">
                      Available Credits
                    </label>
                    <p className="text-[var(--success)] border-b border-[var(--border-color)] pb-1 flex items-center gap-2">
                      {totalCredits.toLocaleString()}
                      <span className="text-[10px] opacity-50">CR</span>
                    </p>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] uppercase text-[var(--text-subtle)] tracking-wider">
                      Contact Point
                    </label>
                    <p className="text-[var(--text-main)] border-b border-[var(--border-color)] pb-1 truncate">
                      {organization?.email}
                    </p>
                  </div>
                </div>

                {/* Visual Filler */}
                <div className="mt-8 opacity-20 text-[10px]">
                  <div className="flex justify-between">
                    <span>SECURE_CONN_ESTABLISHED</span>
                    <span>ENCRYPTION: AES-256</span>
                  </div>
                  <div className="w-full h-1 bg-[var(--text-subtle)] mt-1 relative overflow-hidden">
                    <div className="absolute top-0 left-0 h-full w-1/3 bg-[var(--primary)] animate-pulse"></div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Activity and Alerts */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
          <ActivityFeed activities={recentActivity} showFilters={false} />
          <AlertsPanel alerts={recentAlerts} showAcknowledged={false} />
        </div>
      </div>

      {/* Onboarding Flow */}
      <OnboardingFlow
        open={showOnboarding}
        onOpenChange={setShowOnboarding}
        onComplete={handleOnboardingComplete}
      />
    </div>
  );
}
